import { useState } from 'react';
import { toast } from 'sonner';
import { X, ArrowUpCircle } from 'lucide-react';
import api from '../lib/api';
import { PLAN_ORDER, PLANS } from '../lib/plans';
import { useAuth } from '../lib/auth';

/**
 * Modal que envía una solicitud de aumento de plan al backoffice de Priviax.
 * `open` / `onClose` controlan visibilidad. `defaultTargetPlan` opcional.
 */
export default function UpgradeRequestModal({ open, onClose, defaultTargetPlan }) {
  const { organization, user } = useAuth();
  const currentLevel = PLANS[organization?.plan || 'pyme']?.level ?? 1;
  const upgradable = PLAN_ORDER.filter((k) => PLANS[k].level > currentLevel);

  const [target, setTarget] = useState(defaultTargetPlan || upgradable[0] || '');
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState(user?.telefono || '');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const isAdmin = user?.role === 'admin';

  const submit = async (e) => {
    e.preventDefault();
    if (!target) return toast.error('Elija un plan objetivo.');
    setSubmitting(true);
    try {
      const { data } = await api.post('/plan/upgrade-request', {
        target_plan: target,
        message: message.trim() || null,
        phone: phone.trim() || null,
      });
      if (data.queued) {
        toast.success(data.message || 'Solicitud enviada a Priviax.');
        onClose(true);
      } else {
        toast.warning(data.message || 'Registramos su solicitud, pero no pudimos enviar el correo.');
        onClose(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos enviar la solicitud.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      data-testid="upgrade-request-modal"
      onClick={() => onClose(false)}
    >
      <div
        className="bg-white w-full max-w-lg border border-[#E5E7EB] rounded-md shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2 text-[#1C2329]">
            <ArrowUpCircle size={18} strokeWidth={1.8} className="text-[#143A34]" />
            <span className="text-[15px] font-semibold">Solicitar aumento de plan</span>
          </div>
          <button
            type="button"
            onClick={() => onClose(false)}
            className="text-[#6B7280] hover:text-[#1C2329]"
            data-testid="upgrade-request-close"
          >
            <X size={16} />
          </button>
        </div>

        {!isAdmin ? (
          <div className="p-5 text-[13px] text-[#6B7280]">
            Solo el Administrador (DPO) puede solicitar el aumento de plan.
            Coordine con quien administra su cuenta.
          </div>
        ) : upgradable.length === 0 ? (
          <div className="p-5 text-[13px] text-[#6B7280]">
            Su organización ya tiene el plan más alto disponible.
            Contáctenos a <b>jaime@priviax.cl</b> para condiciones a la medida.
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <div className="text-[13px] text-[#374151]">
              Enviaremos su solicitud al equipo Priviax. Un ejecutivo se pondrá en contacto
              para coordinar el cambio de plan y la facturación.
            </div>

            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">
                Plan solicitado
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {upgradable.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setTarget(k)}
                    className={`text-left p-3 border rounded-md transition-colors ${
                      target === k ? 'border-[#143A34] bg-[#F0FBF5]' : 'border-[#E5E7EB] bg-white hover:bg-[#FAFBFC]'
                    }`}
                    data-testid={`upgrade-plan-${k}`}
                  >
                    <div className="text-[13px] font-semibold text-[#1C2329]">{PLANS[k].name}</div>
                    <div className="text-[11.5px] text-[#6B7280] mt-0.5 tabular-nums">
                      {PLANS[k].price} · {PLANS[k].size}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">
                Teléfono de contacto (opcional)
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+56 9 …"
                className="pv-input"
                data-testid="upgrade-phone"
              />
            </div>

            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">
                Motivo (opcional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="¿Qué necesita desbloquear? Ej: llegamos al tope de RATs, incorporamos más usuarios, sumamos otro dominio, etc."
                className="pv-input"
                data-testid="upgrade-message"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => onClose(false)}
                disabled={submitting}
                className="pv-btn-secondary"
                data-testid="upgrade-cancel"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="pv-btn-primary flex items-center gap-2"
                data-testid="upgrade-submit"
              >
                {submitting ? 'Enviando…' : 'Enviar solicitud'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
